import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Button, Box, Typography, TextField, Stack, IconButton, 
    Grid, Autocomplete, Dialog, 
    DialogTitle, DialogContent, DialogActions, 
    Divider, Paper, CircularProgress
} from '@mui/material';
import axios from 'axios';
import { 
    FaEdit, FaTrash, FaUpload, FaPalette, FaPlus
} from 'react-icons/fa';
import { MdClose, MdSave, MdDelete} from 'react-icons/md';
import { API_BASE_URL } from '../../App';
import { toast } from 'react-toastify';

import 'react-quill-new/dist/quill.snow.css';
import ConfirmModal from '../../components/ConfirmModal';
import EmailTemplateEditor from './components/EmailTemplateEditor';
import BoardMemberManagement from './components/BoardMemberManagement';
import ContactTopicManagement from './components/ContactTopicManagement';

const ContactAdmin = () => {
    const [confirmConfig, setConfirmConfig] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
        type: 'question'
    });
    const [members, setMembers] = useState([]);
    const [topics, setTopics] = useState([]);
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);

    // Modals
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [showTopicModal, setShowTopicModal] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [editingTopic, setEditingTopic] = useState(null);

    const [memberForm, setMemberForm] = useState({ playerId: '', name: '', position: '', email: '', phone: '', order: 0 });
    const [topicForm, setTopicForm] = useState({ label: '', value: '', recipientEmail: '', hint: '' });
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showDesignModal, setShowDesignModal] = useState(false);
    const [players, setPlayers] = useState([]); // List for search
    const [emailBgColor, setEmailBgColor] = useState('#f4f4f4');
    const [emailAccentColor, setEmailAccentColor] = useState('#d32f2f');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Email Templates
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    
    // Image Gallery
    const [availableImages, setAvailableImages] = useState([]);
    const [showImageGallery, setShowImageGallery] = useState(false);
    
    const fileInputRef = useRef(null);

    const API_BASE = `${API_BASE_URL}/api`;

    const loadTemplate = useCallback((template) => {
        if (!template) return;
        setEmailBgColor(template.backgroundColor || '#f4f4f4');
        setEmailAccentColor(template.accentColor || '#d32f2f');
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [contactRes, playersRes, templatesRes] = await Promise.all([
                axios.get(`${API_BASE}/contact/data`, { withCredentials: true }),
                axios.get(`${API_BASE}/allplayersadmin`, { withCredentials: true }),
                axios.get(`${API_BASE}/email-templates`, { withCredentials: true })
            ]);
            setMembers((contactRes.data.members || []).sort((a, b) => a.order - b.order));
            setTopics(contactRes.data.topics || []);
            const s = contactRes.data.settings || {};
            setSettings(s);
            setEmailBgColor(s.emailBackgroundColor || '#f4f4f4');
            setEmailAccentColor(s.emailAccentColor || '#d32f2f');
            setPlayers(playersRes.data || []);
            
            // Set templates and select first one by default
            setTemplates(templatesRes.data || []);
        } catch (err) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    const fetchAvailableImages = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE}/contact/images`, { withCredentials: true });
            setAvailableImages(response.data || []);
        } catch (err) {
            console.error('Fehler beim Laden der Bildergalerie:', err);
        }
    }, [API_BASE]);

    useEffect(() => {
        fetchData();
        fetchAvailableImages();
    }, [fetchData, fetchAvailableImages]);

    useEffect(() => {
        if (templates.length > 0 && !selectedTemplate) {
            const firstTemplate = templates[0];
            setSelectedTemplate(firstTemplate);
            loadTemplate(firstTemplate);
        }
    }, [templates, selectedTemplate, loadTemplate]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleImageUpload = async () => {
        if (!selectedFile) return;
        const formData = new FormData();
        formData.append('image', selectedFile);
        try {
            await axios.post(`${API_BASE}/contact/upload-image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true
            });
            toast.success('Bild erfolgreich hochgeladen');
            fetchData();
            fetchAvailableImages(); // Refresh gallery
            setSelectedFile(null);
            setPreviewUrl(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err) {
            toast.error('Upload fehlgeschlagen');
        }
    };

    const handleSelectImage = async (imageUrl) => {
        try {
            // Update settings to use this image
            const response = await axios.get(`${API_BASE}/contact/data`, { withCredentials: true });
            let settings = response.data.settings || {};
            
            // Update the boardImagePath
            await axios.put(`${API_BASE}/contact/settings`, {
                ...settings,
                boardImagePath: imageUrl
            }, { withCredentials: true });
            
            toast.success('Bild ausgewählt');
            fetchData(); // Refresh to show new image
        } catch (err) {
            toast.error('Fehler beim Auswählen des Bildes');
        }
    };

    const handleDeleteSpecificImage = (filename) => {
        setConfirmConfig({
            open: true,
            title: 'Bild löschen',
            message: 'Möchtest du dieses Bild wirklich vom Server löschen?',
            onConfirm: () => executeDeleteSpecificImage(filename),
            type: 'delete'
        });
    };

    const executeDeleteSpecificImage = async (filename) => {
        try {
            await axios.delete(`${API_BASE}/contact/images/${filename}`, { withCredentials: true });
            toast.success('Bild gelöscht');
            fetchAvailableImages();
            fetchData(); // Refresh in case it was the active image
        } catch (err) {
            toast.error('Fehler beim Löschen des Bildes');
        }
    };

    const handleDeleteImage = () => {
        setConfirmConfig({
            open: true,
            title: 'Vorstands-Bild löschen',
            message: 'Möchtest du das Vorstands-Bild wirklich löschen und auf das Standardbild zurücksetzen?',
            onConfirm: executeDeleteImage,
            type: 'delete'
        });
    };

    const executeDeleteImage = async () => {
        try {
            await axios.delete(`${API_BASE}/contact/delete-image`, { withCredentials: true });
            toast.success('Bild gelöscht');
            fetchData();
            setSelectedFile(null);
            setPreviewUrl(null);
        } catch (err) {
            toast.error('Fehler beim Löschen des Bildes');
        }
    };

    const handleSaveTemplate = async (updatedTemplate) => {
        try {
            // Priority: updatedTemplate (from editor) > selectedTemplate (state)
            const templateToSave = updatedTemplate || selectedTemplate;
            
            if (!templateToSave) {
                toast.error('Kein Template ausgewählt');
                return;
            }

            const payload = {
                name: templateToSave.name,
                subject: templateToSave.subject,
                htmlContent: templateToSave.htmlContent,
                bodyContent: templateToSave.bodyContent,
                backgroundColor: templateToSave.backgroundColor,
                accentColor: templateToSave.accentColor,
                key: templateToSave.key,
                description: templateToSave.description || '',
                isSystem: templateToSave.isSystem
            };

            if (templateToSave._id) {
                // Update existing template
                const res = await axios.put(`${API_BASE}/email-templates/${templateToSave._id}`, payload, { withCredentials: true });
                setSelectedTemplate(res.data);
                toast.success('Template gespeichert');
            } else {
                // Create new template
                const newTemplate = await axios.post(`${API_BASE}/email-templates`, payload, { withCredentials: true });
                setSelectedTemplate(newTemplate.data);
                toast.success('Template erstellt');
            }
            
            setShowDesignModal(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Speichern fehlgeschlagen');
        }
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        if (template) {
            setEmailBgColor(template.backgroundColor || '#f4f4f4');
            setEmailAccentColor(template.accentColor || '#1976d2');
        }
    };



    const handleDeleteTemplate = (templateId) => {
        setConfirmConfig({
            open: true,
            title: 'Template löschen',
            message: 'Template wirklich löschen?',
            onConfirm: () => executeDeleteTemplate(templateId),
            type: 'delete'
        });
    };

    const executeDeleteTemplate = async (templateId) => {
        try {
            await axios.delete(`${API_BASE}/email-templates/${templateId}`, { withCredentials: true });
            toast.success('Template gelöscht');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Löschen fehlgeschlagen');
        }
    };

    const handleMemberSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            // If playerId is selected, we might want to automatically use their name/email
            // but we'll keep the manual overrides just in case.
            const submissionData = { ...memberForm, playerId: memberForm.playerId || null };
            if (editingMember) {
                await axios.put(`${API_BASE}/contact/members/${editingMember._id}`, submissionData, { withCredentials: true });
                toast.success('Mitglied aktualisiert');
            } else {
                await axios.post(`${API_BASE}/contact/members`, submissionData, { withCredentials: true });
                toast.success('Mitglied erstellt');
            }
            setShowMemberModal(false);
            fetchData();
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Fehler beim Speichern';
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTopicSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (editingTopic) {
                await axios.put(`${API_BASE}/contact/topics/${editingTopic._id}`, topicForm, { withCredentials: true });
                toast.success('Thema aktualisiert');
            } else {
                await axios.post(`${API_BASE}/contact/topics`, topicForm, { withCredentials: true });
                toast.success('Thema erstellt');
            }
            setShowTopicModal(false);
            fetchData();
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Fehler beim Speichern';
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleShowAddMember = () => {
        const nextOrder = members.length > 0 ? Math.max(...members.map(m => m.order || 0)) + 1 : 1;
        setEditingMember(null);
        setMemberForm({ playerId: '', name: '', position: '', email: '', phone: '', order: nextOrder });
        setShowMemberModal(true);
    };

    const handleMoveMember = async (index, direction) => {
        const newMembers = [...members];
        const targetIndex = index + direction;
        
        if (targetIndex < 0 || targetIndex >= newMembers.length) return;

        [newMembers[index], newMembers[targetIndex]] = [newMembers[targetIndex], newMembers[index]];

        const updatedMembers = newMembers.map((m, idx) => ({
            ...m,
            order: idx + 1
        }));

        try {
            setMembers(updatedMembers);
            await axios.post(`${API_BASE}/contact/members/reorder`, {
                members: updatedMembers.map(m => ({ _id: m._id, order: m.order }))
            }, { withCredentials: true });
            toast.success('Reihenfolge gespeichert');
        } catch (err) {
            toast.error('Fehler beim Sortieren');
            fetchData();
        }
    };

    const handleMoveTopic = async (index, direction) => {
        const newTopics = [...topics];
        const targetIndex = index + direction;
        
        if (targetIndex < 0 || targetIndex >= newTopics.length) return;

        [newTopics[index], newTopics[targetIndex]] = [newTopics[targetIndex], newTopics[index]];

        const updatedTopics = newTopics.map((t, idx) => ({
            ...t,
            order: idx + 1
        }));

        try {
            setTopics(updatedTopics);
            await axios.post(`${API_BASE}/contact/topics/reorder`, {
                topics: updatedTopics.map(t => ({ _id: t._id, order: t.order }))
            }, { withCredentials: true });
            toast.success('Reihenfolge gespeichert');
        } catch (err) {
            toast.error('Fehler beim Sortieren');
            fetchData();
        }
    };

    const deleteItem = (type, id, name) => {
        setConfirmConfig({
            open: true,
            title: `${type === 'members' ? 'Vorstandsmitglied' : 'Kontakt-Thema'}`,
            message: <><i>{name}</i> wirklich löschen?</>,
            onConfirm: () => executeDeleteItem(type, id),
            type: 'delete'
        });
    };

    const executeDeleteItem = async (type, id) => {
        try {
            await axios.delete(`${API_BASE}/contact/${type}/${id}`, { withCredentials: true });
            
            if (type === 'members') {
                const res = await axios.get(`${API_BASE}/contact/data`, { withCredentials: true });
                const remainingMembers = (res.data.members || []).sort((a, b) => a.order - b.order);
                const updatedMembers = remainingMembers.map((m, idx) => ({ ...m, order: idx + 1 }));
                
                await axios.post(`${API_BASE}/contact/members/reorder`, {
                    members: updatedMembers.map(m => ({ _id: m._id, order: m.order }))
                }, { withCredentials: true });
            } else if (type === 'topics') {
                const res = await axios.get(`${API_BASE}/contact/data`, { withCredentials: true });
                const remainingTopics = (res.data.topics || []).sort((a, b) => a.order - b.order);
                const updatedTopics = remainingTopics.map((t, idx) => ({ ...t, order: idx + 1 }));
                
                await axios.post(`${API_BASE}/contact/topics/reorder`, {
                    topics: updatedTopics.map(t => ({ _id: t._id, order: t.order }))
                }, { withCredentials: true });
            }

            toast.success('Gelöscht');
            fetchData();
        } catch (err) {
            toast.error('Fehler beim Löschen');
        }
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
            <CircularProgress />
        </Box>
    );

    const getDisplayName = (m) => {
        if (!m) return '';
        const pid = m.playerId;
        // Case 1: Populated Player object
        if (pid && typeof pid === 'object') {
            const fullName = `${pid.firstName || ''} ${pid.lastName || ''}`.trim();
            if (fullName) return fullName;
            if (pid.name) return pid.name;
        }
        // Case 2: Manual name or unpopulated ID
        return m.name || (pid && typeof pid === 'string' ? `ID: ${pid.substring(0, 5)}` : 'Unbekannt');
    };

    const STATIC_BASE = API_BASE_URL;

    const getImageUrl = () => {
        if (previewUrl) return previewUrl;
        if (!settings.boardImagePath) return null;
        if (settings.boardImagePath.startsWith('http')) return settings.boardImagePath;
        if (settings.boardImagePath.startsWith('uploads')) return `${STATIC_BASE}/${settings.boardImagePath}`;
        return null; // No default image
    };

    return (
        <Box>
            {/* Board Image Section */}
            <Paper 
                elevation={0} 
                sx={{ 
                    mb: 4, 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(0,0,0,0.05)'
                }}
            >
                <Box sx={{ 
                    p: 2, 
                    borderBottom: '1px solid rgba(0,0,0,0.05)',
                    bgcolor: 'rgba(0,0,0,0.01)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Typography variant="h6" color="primary">Vorstands-Bild</Typography>
                </Box>

                <Box sx={{ p: 0 }}>
                    <Grid container sx={{ minHeight: 220 }}>
                        <Grid item xs={12} md={5} sx={{ 
                            p: 3, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: '#fcfcfc',
                            borderRight: { md: '1px solid #f0f0f0' }
                        }}>
                            {getImageUrl() ? (
                                <Box sx={{ position: 'relative', width: '100%', maxWidth: 400 }}>
                                    <Box 
                                        component="img"
                                        src={getImageUrl()} 
                                        alt="Vorstand" 
                                        sx={{ 
                                            width: '100%',
                                            maxHeight: 240,
                                            objectFit: 'contain',
                                            borderRadius: 2,
                                            bgcolor: '#eee',
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                            border: '6px solid white',
                                            transition: 'transform 0.3s ease',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    />
                                    {previewUrl && (
                                        <Box sx={{ 
                                            position: 'absolute', top: -10, right: -10,
                                            bgcolor: 'warning.main', color: 'warning.contrastText',
                                            px: 1.5, py: 0.5, borderRadius: 2, fontSize: '0.7rem', fontWeight: 'bold',
                                            boxShadow: 2, zIndex: 2
                                        }}>
                                            VORSCHAU
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    width: '100%', height: 200, maxWidth: 320,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: '#f0f2f5', border: '2px dashed #ccc', borderRadius: 3, p: 4
                                }}>
                                    <Typography variant="body2" color="text.secondary">Kein Bild konfiguriert</Typography>
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={12} md={7} sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Stack spacing={2}>

                                <Box sx={{ 
                                    p: 2, borderRadius: 2, bgcolor: '#f8f9fa', 
                                    border: '1px solid #eee', position: 'relative'
                                }}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            color="primary"
                                            startIcon={selectedFile ? <FaEdit /> : <FaPlus />}
                                            sx={{ borderRadius: 2, textTransform: 'none', px: 2, py: 0.5, whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                                        >
                                            {selectedFile ? 'Anderes Bild' : 'Neues Bild wählen'}
                                            <input
                                                type="file"
                                                hidden
                                                onChange={handleFileChange}
                                                accept="image/*"
                                                ref={fileInputRef}
                                            />
                                        </Button>
                                        
                                        {selectedFile && (
                                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', wordBreak: 'break-all' }}>
                                                {selectedFile.name}
                                            </Typography>
                                        )}
                                        
                                        {!selectedFile && (
                                            <Typography variant="caption" color="text.secondary">
                                                Max. 5MB, JPG/PNG empfohlen
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                                
                                <Stack direction="row" spacing={1.5}>
                                    <Button 
                                        variant="contained" 
                                        color="error" 
                                        disabled={!selectedFile} 
                                        onClick={handleImageUpload}
                                        startIcon={<FaUpload />}
                                        sx={{ borderRadius: 2, px: 2, py: 0.5, boxShadow: 'none', '&:hover': { boxShadow: 2 }, fontSize: '0.8rem', textTransform: 'none' }}
                                    >
                                        Hochladen & Aktivieren
                                    </Button>
                                    
                                    {settings.boardImagePath && !selectedFile && (
                                        <Button 
                                            variant="text" 
                                            color="error" 
                                            onClick={handleDeleteImage}
                                            startIcon={<FaTrash />}
                                            sx={{ 
                                                textTransform: 'none',
                                                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                                                minWidth: { xs: 'auto', sm: '64px' }
                                            }}
                                        >
                                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                                Bild entfernen
                                            </Box>
                                        </Button>
                                    )}

                                    {selectedFile && (
                                        <Button 
                                            variant="text" 
                                            color="inherit" 
                                            onClick={() => { 
                                                setSelectedFile(null); 
                                                setPreviewUrl(null); 
                                                if (fileInputRef.current) fileInputRef.current.value = '';
                                            }}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Abbrechen
                                        </Button>
                                    )}
                                </Stack>

                                <Divider />

                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <Button 
                                        variant="outlined" 
                                        color="inherit" 
                                        size="small" 
                                        onClick={() => setShowImageGallery(!showImageGallery)}
                                        sx={{ 
                                            textTransform: 'none', 
                                            borderColor: '#ddd',
                                            borderRadius: 2,
                                            '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'transparent' }
                                        }}
                                        startIcon={<FaPalette />}
                                    >
                                        {showImageGallery ? 'Galerie einklappen' : `Galerie (${availableImages.length})`}
                                    </Button>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                    
                    {/* Image Gallery */}
                    {showImageGallery && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 1.5 }} />
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>Bildergalerie</Typography>
                            {availableImages.length === 0 ? (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Keine Bilder vorhanden</Typography>
                            ) : (
                                <Grid container spacing={2}>
                                    {availableImages.map((image) => {
                                        const isActive = settings.boardImagePath === image.url;
                                        return (
                                            <Grid key={image.filename} item xs={6} sm={4} md={3} lg={2}>
                                                <Box 
                                                    sx={{ 
                                                        position: 'relative', borderRadius: 2, 
                                                        border: isActive ? '3px solid #d32f2f' : '1px solid #dee2e6',
                                                        overflow: 'hidden', cursor: 'pointer',
                                                        transition: 'transform 0.2s',
                                                        '&:hover': { transform: 'scale(1.02)' }
                                                    }}
                                                    onClick={() => handleSelectImage(image.url)}
                                                >
                                                    <Box 
                                                        component="img"
                                                        src={`${STATIC_BASE}/${image.url}`}
                                                        alt={image.filename}
                                                        sx={{ height: 100, width: '100%', objectFit: 'contain', bgcolor: '#f0f0f0' }}
                                                    />
                                                    {isActive && (
                                                        <Box sx={{ 
                                                            position: 'absolute', top: 4, left: 4,
                                                            bgcolor: 'error.main', color: 'white',
                                                            px: 0.75, py: 0.2, borderRadius: 1, fontSize: '0.6rem', fontWeight: 'bold'
                                                        }}>
                                                            AKTIV
                                                        </Box>
                                                    )}
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        sx={{ 
                                                            position: 'absolute', bottom: 4, right: 4,
                                                            bgcolor: 'rgba(255,255,255,0.9)',
                                                            '&:hover': { bgcolor: 'white' }
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteSpecificImage(image.filename);
                                                        }}
                                                    >
                                                        <FaTrash fontSize="0.7rem" />
                                                    </IconButton>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Box>
                    )}
                </Box>
            </Paper>

            <Grid container spacing={4}>
                {/* Members Section */}
                <Grid item xs={12} lg={7}>
                    <BoardMemberManagement 
                        members={members}
                        onAdd={handleShowAddMember}
                        onEdit={(m) => { 
                            setEditingMember(m); 
                            setMemberForm({ 
                                playerId: m.playerId?._id || m.playerId || '', 
                                name: m.name || '', 
                                position: m.position || '', 
                                email: m.email || '', 
                                phone: m.phone || '', 
                                order: m.order || 1 
                            }); 
                            setShowMemberModal(true); 
                        }}
                        onDelete={deleteItem}
                        onMove={handleMoveMember}
                        getDisplayName={getDisplayName}
                    />
                </Grid>

                {/* Topics Section */}
                <Grid item xs={12} lg={5}>
                    <ContactTopicManagement 
                        topics={topics}
                        onAdd={() => { setEditingTopic(null); setTopicForm({ label: '', value: '', recipientEmail: '', hint: '' }); setShowTopicModal(true); }}
                        onEdit={(t) => { setEditingTopic(t); setTopicForm(t); setShowTopicModal(true); }}
                        onDelete={deleteItem}
                        onMove={handleMoveTopic}
                    />
                </Grid>
            </Grid>

            {/* Email Templates Section */}
            <Paper 
                elevation={0} 
                sx={{ 
                    mb: 4, 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(0,0,0,0.05)'
                }}
            >
                <Box sx={{ 
                    p: 2, 
                    borderBottom: '1px solid rgba(0,0,0,0.05)',
                    bgcolor: 'rgba(0,0,0,0.01)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <FaPalette color="#d32f2f" />
                    <Typography variant="h6" color="primary">E-Mail Templates</Typography>
                </Box>

                <Box sx={{ pt: 3, position: 'relative' }}>
                    <Box sx={{ 
                        display: 'flex', 
                        overflowX: 'auto', 
                        justifyContent: { xs: 'flex-start', md: 'center' },
                        pb: 3,
                        px: { xs: 2, md: 0 },
                        gap: { xs: 2, md: 3 },
                        scrollSnapType: 'x mandatory',
                        // Force visible scrollbar for mobile usability hints
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 4, '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' } }
                    }}>
                        {templates.map(template => (
                            <Box key={template._id} sx={{ 
                                minWidth: { xs: '260px', sm: '300px', md: '30%' }, // Slightly smaller for better fit
                                maxWidth: { xs: '260px', sm: '300px', md: '30%' }, // Enforce max width to prevent one card from being larger
                                scrollSnapAlign: 'start',
                                flexShrink: 0
                            }}>
                                <Paper 
                                    elevation={0}
                                    sx={{ 
                                        height: '100%', 
                                        border: selectedTemplate?._id === template._id ? '2px solid #d32f2f' : '1px solid rgba(0,0,0,0.05)',
                                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                                        borderRadius: '10px',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': { 
                                            boxShadow: '0 8px 15px -5px rgba(0,0,0,0.1)', 
                                            cursor: 'pointer',
                                            transform: 'translateY(-2px)',
                                            bgcolor: 'white'
                                        }
                                    }}
                                >
                                    <Box onClick={() => handleTemplateSelect(template)} sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '70%' }}>
                                                {template.name}
                                            </Typography>
                                            {template.isSystem && (
                                                <Box component="span" sx={{ 
                                                    bgcolor: '#f1f3f5', color: '#495057', fontSize: '0.65rem',
                                                    px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 'bold'
                                                }}>SYSTEM</Box>
                                            )}
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ 
                                            display: '-webkit-box', 
                                            WebkitLineClamp: 2, 
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            minHeight: 36, 
                                            lineHeight: 1.2,
                                            mb: 1.5 
                                        }}>
                                            {template.description || template.subject}
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Box sx={{ width: 16, height: 16, bgcolor: template.accentColor, borderRadius: 0.5, border: '1px solid #ddd' }} />
                                            <Box sx={{ width: 16, height: 16, bgcolor: template.backgroundColor, borderRadius: 0.5, border: '1px solid #ddd' }} />
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{template.key}</Typography>
                                        </Stack>
                                    </Box>
                                    <Divider />
                                    <Stack direction="row" spacing={1} sx={{ p: 1, justifyContent: 'flex-end' }}>
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            startIcon={<FaEdit />} 
                                            onClick={(e) => { e.stopPropagation(); handleTemplateSelect(template); setShowDesignModal(true); }}
                                            sx={{ fontSize: '0.7rem', py: 0.25 }}
                                        >
                                            Design
                                        </Button>
                                        {!template.isSystem && (
                                            <IconButton 
                                                size="small" 
                                                color="error" 
                                                onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template._id); }}
                                            >
                                                <FaTrash fontSize="0.8rem" />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Paper>

            {/* Member Modal */}
            <Dialog open={showMemberModal} onClose={() => setShowMemberModal(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ 
                    bgcolor: 'primary.main', color: 'white', px: 3, py: 2,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <Typography variant="h6" component="div" fontWeight="bold">
                        {editingMember ? 'Vorstandsmitglied bearbeiten' : 'Neues Vorstandsmitglied'}
                    </Typography>
                    <IconButton onClick={() => setShowMemberModal(false)} sx={{ color: 'white' }} size="small">
                        <MdClose />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pt: 3 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>VERKNÜPFUNG (OPTIONAL)</Typography>
                            <Autocomplete
                                options={players.filter(p => p.email !== 'gast@tc-opfingen.de')}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return `${option.lastName || ''}, ${option.firstName || ''} (${option.email || 'Keine Mail'})`.trim();
                                }}
                                value={players.find(p => String(p._id || p.id) === String(memberForm.playerId)) || null}
                                onChange={(_, newValue) => {
                                    if (!newValue) {
                                        setMemberForm(prev => ({ ...prev, playerId: '' }));
                                        return;
                                    }
                                    const pId = newValue._id || newValue.id;
                                    const pName = newValue.name || `${newValue.firstName || ''} ${newValue.lastName || ''}`.trim();
                                    setMemberForm(prev => ({
                                        ...prev, 
                                        playerId: pId,
                                        name: pName || prev.name,
                                        email: newValue.email || prev.email
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Mitglied suchen" 
                                        size="small" 
                                    />
                                )}
                            />
                        </Box>

                        <Divider />

                        <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>ANZEIGEDATEN (MANUELL ÜBERSCHREIBBAR)</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Name (Vollständig)"
                                    fullWidth
                                    size="small"
                                    required
                                    value={memberForm.name}
                                    onChange={e => setMemberForm({...memberForm, name: e.target.value})}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Position (z.B. 1. Vorstand)"
                                    fullWidth
                                    size="small"
                                    required
                                    value={memberForm.position}
                                    onChange={e => setMemberForm({...memberForm, position: e.target.value})}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="E-Mail"
                                    type="email"
                                    fullWidth
                                    size="small"
                                    value={memberForm.email || ''}
                                    onChange={e => setMemberForm({...memberForm, email: e.target.value})}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Telefon"
                                    fullWidth
                                    size="small"
                                    value={memberForm.phone || ''}
                                    onChange={e => setMemberForm({...memberForm, phone: e.target.value})}
                                />
                            </Grid>
                        </Grid>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa', justifyContent: 'space-between' }}>
                    <Box>
                        {editingMember && (
                            <Button 
                                color="error" 
                                startIcon={<MdDelete />}
                                onClick={() => {
                                    setShowMemberModal(false);
                                    deleteItem('members', editingMember._id, memberForm.name);
                                }}
                            >
                                Löschen
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setShowMemberModal(false)} color="inherit">Abbrechen</Button>
                        <Button 
                            variant="contained" 
                            startIcon={<MdSave />}
                            onClick={handleMemberSubmit}
                            disabled={isSubmitting}
                            sx={{ px: 3 }}
                        >
                            {isSubmitting ? 'Speichern...' : 'Speichern'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Topic Modal */}
            <Dialog open={showTopicModal} onClose={() => setShowTopicModal(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ 
                    bgcolor: 'primary.main', color: 'white', px: 3, py: 2,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <Typography variant="h6" component="div" fontWeight="bold">
                        {editingTopic ? 'Thema bearbeiten' : 'Neues Thema'}
                    </Typography>
                    <IconButton onClick={() => setShowTopicModal(false)} sx={{ color: 'white' }} size="small">
                        <MdClose />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pt: 3 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Bezeichnung (Anzeige)"
                            fullWidth
                            size="small"
                            required
                            value={topicForm.label}
                            onChange={e => setTopicForm({...topicForm, label: e.target.value})}
                        />
                        <TextField
                            label="Interner Wert (ID)"
                            fullWidth
                            size="small"
                            required
                            value={topicForm.value}
                            onChange={e => setTopicForm({...topicForm, value: e.target.value})}
                        />
                        <TextField
                            label="Hinweis (optional)"
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            value={topicForm.hint || ''}
                            onChange={e => setTopicForm({...topicForm, hint: e.target.value})}
                            placeholder="Optionaler Hinweis für dieses Thema..."
                        />
                        <TextField
                            label="Ziel E-Mail"
                            type="email"
                            fullWidth
                            size="small"
                            required
                            value={topicForm.recipientEmail}
                            onChange={e => setTopicForm({...topicForm, recipientEmail: e.target.value})}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa', justifyContent: 'space-between' }}>
                    <Box>
                        {editingTopic && (
                            <Button 
                                color="error" 
                                startIcon={<MdDelete />}
                                onClick={() => {
                                    setShowTopicModal(false);
                                    deleteItem('topics', editingTopic._id, topicForm.label);
                                }}
                            >
                                Löschen
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setShowTopicModal(false)} color="inherit">Abbrechen</Button>
                        <Button 
                            variant="contained" 
                            startIcon={<MdSave />}
                            onClick={handleTopicSubmit}
                            disabled={isSubmitting}
                            sx={{ px: 3 }}
                        >
                            {isSubmitting ? 'Speichern...' : 'Speichern'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            
            <EmailTemplateEditor 
                key={selectedTemplate?._id || 'new-template'}
                show={showDesignModal}
                onHide={() => setShowDesignModal(false)}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                emailBgColor={emailBgColor}
                setEmailBgColor={setEmailBgColor}
                emailAccentColor={emailAccentColor}
                setEmailAccentColor={setEmailAccentColor}
                handleSaveTemplate={handleSaveTemplate}
            />

            <ConfirmModal 
                open={confirmConfig.open}
                onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
            />
        </Box>
    );
};

export default ContactAdmin;
